import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import { Close, EditCalendarRounded, ArrowForwardRounded } from '@mui/icons-material';
import { format } from 'date-fns';
import { LeaveRequest } from '../../services/leaveService';

interface EditLeaveDatesDialogProps {
  open: boolean;
  request: LeaveRequest | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (payload: { startDate: string; endDate: string; editReason: string }) => Promise<void> | void;
}

// Strips a YYYY-MM-DD slice out of a Date / ISO string for the native date
// inputs. We use YYYY-MM-DD only — no timezone conversion needed because the
// server interprets the calendar day in the school's TZ either way.
const toIsoDay = (d: Date | string): string => {
  const dt = typeof d === 'string' ? new Date(d) : d;
  const yyyy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/**
 * Edit the date range of an APPROVED leave. The server is the source of
 * truth for the recomputed `totalDays` (it applies sandwich rule + the
 * school's weekly offs + holidays), so this dialog only collects the new
 * range + a reason. We surface the *current* `totalDays` so the admin sees
 * what they're moving away from; the server returns the new value.
 *
 * Validation here is intentionally minimal — start ≤ end and "at least one
 * field changed" — everything else (cycle boundary, quota, overlap, max
 * consecutive days) is enforced by the backend so the rules can't drift.
 */
const EditLeaveDatesDialog: React.FC<EditLeaveDatesDialogProps> = ({
  open,
  request,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (open && request) {
      setStartDate(toIsoDay(request.startDate));
      setEndDate(toIsoDay(request.endDate));
      setReason('');
      setLocalError(null);
    }
  }, [open, request]);

  const employeeName = useMemo(
    () => (request && typeof request.employeeId === 'object' ? request.employeeId.name : 'Unknown'),
    [request]
  );
  const leaveTypeName = useMemo(
    () => (request && typeof request.leaveTypeId === 'object' ? request.leaveTypeId.name : 'Unknown'),
    [request]
  );

  if (!request) return null;

  const originalStart = toIsoDay(request.startDate);
  const originalEnd = toIsoDay(request.endDate);
  const datesChanged = startDate !== originalStart || endDate !== originalEnd;

  const handleSubmit = () => {
    setLocalError(null);
    if (!startDate || !endDate) {
      setLocalError('Both start and end dates are required.');
      return;
    }
    if (startDate > endDate) {
      setLocalError('End date cannot be before start date.');
      return;
    }
    if (!datesChanged) {
      setLocalError('The dates are unchanged — nothing to save.');
      return;
    }
    void onConfirm({ startDate, endDate, editReason: reason.trim() });
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box display="flex" alignItems="center" gap={1.25}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
            }}
          >
            <EditCalendarRounded fontSize="small" />
          </Box>
          <Typography variant="h6" fontWeight={600}>Edit Leave Dates</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={loading}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Card variant="outlined" sx={{ mb: 2, bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
          <CardContent sx={{ py: 1.75 }}>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Employee</Typography>
                <Typography variant="body2" fontWeight={600}>{employeeName}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Leave Type</Typography>
                <Typography variant="body2" fontWeight={600}>{leaveTypeName}</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">Currently Approved</Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.25}>
                  <Typography variant="body2">
                    {format(new Date(request.startDate), 'MMM dd, yyyy')} – {format(new Date(request.endDate), 'MMM dd, yyyy')}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${request.totalDays} day${request.totalDays > 1 ? 's' : ''}`}
                    sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="New Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }} sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'center' }}>
            <ArrowForwardRounded color="action" />
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="New End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
          </Grid>
        </Grid>

        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          label="Reason for change (recommended)"
          placeholder="e.g. Employee requested a different week / overlap with another leave"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={loading}
          sx={{ mb: localError ? 1.5 : 0 }}
        />

        {localError && (
          <Alert severity="error" sx={{ mb: 0 }}>
            {localError}
          </Alert>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          The leave-day total will be <strong>recomputed</strong> based on the policy's sandwich rule, weekly offs, and holidays. The employee's balance will be adjusted by the difference.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !datesChanged}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <EditCalendarRounded />}
        >
          {loading ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLeaveDatesDialog;
